import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import 'intl-pluralrules';

import { configureSentryScope } from 'sentry';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'utils/constants';

import arLocale from './locales/ar.json';
import deLocale from './locales/de.json';
import enLocale from './locales/en.json';
import esLocale from './locales/es.json';
import frLocale from './locales/fr.json';
import hiLocale from './locales/hi.json';
import idLocale from './locales/id.json';
import itLocale from './locales/it.json';
import jaLocale from './locales/ja.json';
import kmLocale from './locales/km.json';
import koLocale from './locales/ko.json';
import loLocale from './locales/lo.json';
import msLocale from './locales/ms.json';
import nlLocale from './locales/nl.json';
import ptLocale from './locales/pt.json';
import ruLocale from './locales/ru.json';
import thLocale from './locales/th.json';
import tlLocale from './locales/tl.json';
import trLocale from './locales/tr.json';
import viLocale from './locales/vi.json';
import zhCNLocale from './locales/zh-CN.json';
import zhTWLocale from './locales/zh-TW.json';

i18next.use(initReactI18next).use(LanguageDetector);

export async function initI18n(): Promise<typeof i18next> {
  if (!i18next.isInitialized) {
    await i18next.init({
      resources: {
        ar: { translations: arLocale },
        de: { translations: deLocale },
        en: { translations: enLocale },
        es: { translations: esLocale },
        fr: { translations: frLocale },
        hi: { translations: hiLocale },
        id: { translations: idLocale },
        it: { translations: itLocale },
        ja: { translations: jaLocale },
        km: { translations: kmLocale },
        ko: { translations: koLocale },
        lo: { translations: loLocale },
        ms: { translations: msLocale },
        nl: { translations: nlLocale },
        pt: { translations: ptLocale },
        ru: { translations: ruLocale },
        th: { translations: thLocale },
        tl: { translations: tlLocale },
        tr: { translations: trLocale },
        vi: { translations: viLocale },
        'zh-CN': { translations: zhCNLocale },
        'zh-TW': { translations: zhTWLocale }
      },
      partialBundledLanguages: true,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      ns: ['translations'],
      defaultNS: 'translations',
      debug: CONFIG.isDevelopment,
      interpolation: {
        escapeValue: false // not needed for react!!
      },
      react: {
        useSuspense: false
      },
      detection: {
        order: ['path'],
        lookupFromPathIndex: 0
      }
    });
  }

  configureSentryScope((scope) => {
    scope.setTag('locale', i18next.language);
  });

  return i18next;
}

i18next.on('languageChanged', (newLanguage) => {
  configureSentryScope((scope) => {
    scope.setTag('locale', newLanguage);
  });
});

export function getI18nextInstance(): typeof i18next {
  if (!i18next.isInitialized)
    throw new Error("i18next hasn't been initialized yet");
  return i18next;
}
